import {
  ChangeEvent, CSSProperties, useEffect, useMemo, useState,
} from 'react';
import Button from '../../../../Components/Button';
import Drawer from '../../../../Components/Drawer';
import Input from '../../../../Components/Input';
import USDTGroup from '../../../../assets/images/usdt/usdt-group.png';
import USDTIcon from '../../../../assets/images/usdt/usdt-icon-lg.png';
import WithdrawShine from '../../../../assets/images/withdrawal/withdraw-shine.png';
import { formatNumbers } from '../../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getCharacterSkinNames } from '../../../../store/reducers/profile/asyncActions';
import useConnectWallet from '../../../../hooks/useConnectWallet';

type TWithdrawModalProps = {
    open: boolean;
    onClose: () => void;
    onWithdraw: (form: TWithdrawFormState) => void;
}

export type TWithdrawFormState = {
    error: {
        usdt: null | string
    };
    state: {
        usdt: string;
    }
}

const initialFormState: TWithdrawFormState = {
  error: {
    usdt: null,
  },
  state: {
    usdt: '',
  },
};

function WithdrawModal({ onClose: closeHandler, open, onWithdraw: withdrawHandler }: TWithdrawModalProps) {
  const dispatch = useAppDispatch();
  const { userData, characterSkinNames } = useAppSelector(({ profile }) => profile);
  const { settings: settingsData } = useAppSelector(({ settings }) => settings);

  const { onConnect, walletAddress } = useConnectWallet();

  const [withdrawFormState, setWithdrawFormState] = useState<TWithdrawFormState>(initialFormState);
  useEffect(() => {
    dispatch(getCharacterSkinNames());
  }, []);
  const isWithdrawDisabled = useMemo(() => {
    if (!userData) {
      return true;
    }

    const usdtBalance = userData.usdt_balance / 10 ** 6;
    const toWithdraw = parseFloat(withdrawFormState.state.usdt) || 0;
    const userCharacterLvl = userData?.character_progress.level ?? 1;
    const minAviatorLevel = settingsData?.min_aviator_level ?? 1;
    const minWithdrawAmount = settingsData?.min_withdraw_amount ?? 0;

    if (usdtBalance < toWithdraw || userCharacterLvl < minAviatorLevel || toWithdraw < minWithdrawAmount) {
      return true;
    }

    return false;
  }, [withdrawFormState]);

  const onAmountChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value.replace(',', '.');
    if (/^\d*\.?\d*$/g.test(value)) {
      setWithdrawFormState((prev) => {
        let error = null;

        if (userData && userData.usdt_balance / 10 ** 6 < parseFloat(value)) {
          error = 'Not enough USDT';
        }

        return {
          error: {
            ...prev.error,
            usdt: error,
          },
          state: {
            ...prev.state,
            usdt: evt.target.value,
          },
        };
      });
    }
  };

  const onClose = () => {
    closeHandler();

    setWithdrawFormState(initialFormState);
  };

  const onWithdraw = () => {
    withdrawHandler(withdrawFormState);

    setWithdrawFormState(initialFormState);
  };

  const levelToReach = characterSkinNames[Math.max((settingsData?.min_aviator_level || 1) - 1, 0)];

  const renderProgress = () => {
    if (!settingsData) {
      return null;
    }

    const userLvl = userData?.character_progress.level ?? 1;

    const progress = ((userLvl - 1) / (settingsData.min_aviator_level - 1)) * 100;

    return (
      <div className="max-w-[263px] mx-auto font-Bangers w-full">
        <div className="flex justify-between items-center text-xs">
          <span>
            {characterSkinNames[Math.max(userLvl - 1, 0)]}
          </span>
          <span>
            {levelToReach}
          </span>
        </div>
        <div className={`loader-progress max-w-full w-progress mt-1 ${progress < 100 ? 'invalid' : ''}`} style={{ '--progress': `${Math.min(progress, 100).toFixed(2)}%` } as CSSProperties} />
      </div>
    );
  };

  if (!userData) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="bottom"
      closeStroke="gray-10"
      wrapperClassName="relative px-4 pb-6 bg-withdrawModalBg rounded-t-[20px] overflow-hidden text-[white]"
    >
      <div
        className="
            font-Bangers
            flex flex-col items-center
        "
      >
        <div
          className="
            z-[1]
            flex flex-col
            transform translate-y-[-10%]
          "
        >
          <img
            src={USDTIcon}
            alt="usdt-icon"
            className="
                max-w-[125px]
                transform translate-y-[20%]
            "
          />
          <span className="text-[32px]">USDT</span>
          <span className="text-lg">balance</span>
        </div>
        <img
          src={WithdrawShine}
          alt="withdraw-shine"
          className="
            absolute
            inset-0
          "
        />
      </div>
      {renderProgress()}
      <div
        className="
            flex items-center justify-between
            px-5
            bg-withdrawModalCardBg
            rounded-[12px]
            gradient-border primary before:rounded-[12px] z-[1]
            mt-6
        "
      >
        <div className="flex flex-col text-start gap-0.5">
          <span className="font-Bangers leading-[19px] text-lg">tOTAL tEATHER EARNED</span>
          {!!settingsData && (
          <span className="leading-[14px] text-xs max-w-[139px] text-[#B5C4FC] font-Inria">
            Needs minimum of
            {' '}
            {settingsData.min_withdraw_amount}
            $ USDT for withdraw
          </span>
          )}
        </div>
        <div className="relative p-2">
          <img src={USDTGroup} alt="usdt-group" className="h-[60px]" />
          <span
            className="
                absolute
                bottom-0
                left-[70%]
                font-Bangers
                text-[22px]
                translate-x-[-50%]
            "
          >
            {formatNumbers(userData.usdt_balance / 10 ** 6, 2, false)}
          </span>
        </div>
      </div>
      <div className="mt-6">
        <span className="font-Bangers text-sm">Set the amount TO WITHDRAW</span>
        <Input
          adornment="USDT"
          className="mt-4"
          placeholder="Enter amount"
          type="text"
          inputMode="decimal"
          pattern="[0-9]*[.,]?[0-9]*"
          value={withdrawFormState.state.usdt}
          error={withdrawFormState.error.usdt}
          onChange={onAmountChange}
        />
      </div>
      {walletAddress
        ? (
          <Button className="btn navy py-4 text-[24px] mt-6" disabled={isWithdrawDisabled} onClick={onWithdraw} pureClass>
            {/* eslint-disable-next-line no-nested-ternary */}
            {(settingsData?.min_aviator_level || 1) > (userData?.character_progress.level || 1) ? `reach level ${levelToReach}` : 'Withdraw'}
          </Button>
        )
        : (
          <Button className="btn navy py-4 text-[24px] mt-6" onClick={onConnect} pureClass>
            Connect wallet
          </Button>
        )}

    </Drawer>
  );
}

export default WithdrawModal;
